import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { ListingCard } from "@/components/ListingCard";
import { CardSkeletonGrid, EmptyState, ErrorState } from "@/components/common";
import { Search, ShieldCheck, PackageOpen, LayoutGrid, SlidersHorizontal } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { favoriteIds, toggleFavorite } = useFavorites();
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  // Committed advanced filters (applied to the query)
  const [filters, setFilters] = useState({ condition: "", minPrice: "", maxPrice: "", sort: "newest" });
  // Draft values edited inside the Filters popover
  const [draft, setDraft] = useState(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeCount =
    (filters.condition ? 1 : 0) +
    (filters.minPrice ? 1 : 0) +
    (filters.maxPrice ? 1 : 0) +
    (filters.sort !== "newest" ? 1 : 0);

  const applyFilters = () => { setFilters(draft); setFiltersOpen(false); };
  const clearFilters = () => {
    const reset = { condition: "", minPrice: "", maxPrice: "", sort: "newest" };
    setDraft(reset); setFilters(reset); setFiltersOpen(false);
  };

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => apiFetch("/categories") });

  const { data: listings, isLoading, isError, refetch } = useQuery({
    queryKey: ["listings", category, query, verifiedOnly, filters],
    queryFn: () => {
      const p = new URLSearchParams();
      if (category !== "All") p.set("category", category);
      if (query) p.set("search", query);
      if (verifiedOnly) p.set("verified_only", "true");
      if (filters.condition) p.set("condition", filters.condition);
      if (filters.minPrice) p.set("min_price", filters.minPrice);
      if (filters.maxPrice) p.set("max_price", filters.maxPrice);
      if (filters.sort && filters.sort !== "newest") p.set("sort", filters.sort);
      return apiFetch(`/listings?${p.toString()}`);
    },
  });

  const chips = ["All", ...categories];

  const onFav = (id) => {
    if (!user) {
      if (window.confirm("Sign in to save listings. Go to sign in?")) navigate("/login");
      return;
    }
    toggleFavorite(id);
  };

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-border bg-brand">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
            Your online market
          </h1>
          <p className="mt-2 max-w-lg text-white/70">
            Buy and sell electronics, vehicles, fashion, property and more across Zambia.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }}
            className="mt-6 flex max-w-xl items-center gap-2 rounded-full bg-white p-1.5 shadow-lg"
          >
            <Search className="ml-3 h-5 w-5 text-muted-foreground" />
            <input
              data-testid="home-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search for anything…"
              className="flex-1 bg-transparent px-1 py-2 text-sm outline-none"
            />
            <button data-testid="home-search-btn" type="submit" className="rounded-full bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-400">
              Search
            </button>
          </form>
          <button
            data-testid="browse-btn"
            type="button"
            onClick={() => document.getElementById("listings-feed")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/20"
          >
            <LayoutGrid className="h-4 w-4" /> Browse all listings
          </button>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-16 z-30 border-b border-border bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6">
          <form
            onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1.5"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              data-testid="sticky-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-28 bg-transparent text-sm outline-none sm:w-44"
            />
          </form>
          {chips.map((c) => (
            <button
              key={c}
              data-testid={`category-${c}`}
              onClick={() => setCategory(c)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
                category === c ? "bg-brand text-white" : "bg-secondary text-foreground hover:bg-brand-soft"
              }`}
            >
              {c}
            </button>
          ))}
          <button
            data-testid="verified-filter"
            onClick={() => setVerifiedOnly((v) => !v)}
            className={`ml-auto flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
              verifiedOnly ? "bg-sky-600 text-white" : "bg-secondary text-foreground hover:bg-brand-soft"
            }`}
          >
            <ShieldCheck className="h-4 w-4" /> Verified
          </button>
          <button
            data-testid="open-filters"
            onClick={() => { setDraft(filters); setFiltersOpen((o) => !o); }}
            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
              activeCount || filtersOpen ? "bg-brand text-white" : "bg-secondary text-foreground hover:bg-brand-soft"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" /> Filters
            {activeCount > 0 && (
              <span className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-price text-[11px] font-bold text-white">{activeCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Filters dropdown (self-managed to avoid focus-trap issues with native selects) */}
      {filtersOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} data-testid="filters-backdrop" />
          <div className="fixed right-3 top-[124px] z-50 w-72 space-y-4 rounded-xl border border-border bg-white p-4 shadow-xl sm:right-6" data-testid="filters-panel">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Sort by</label>
              <select
                data-testid="filter-sort"
                value={draft.sort}
                onChange={(e) => setDraft((d) => ({ ...d, sort: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none focus:border-price"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Condition</label>
              <select
                data-testid="filter-condition"
                value={draft.condition}
                onChange={(e) => setDraft((d) => ({ ...d, condition: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none focus:border-price"
              >
                <option value="">Any condition</option>
                <option value="New">New</option>
                <option value="Used">Used</option>
                <option value="Refurbished">Refurbished</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Price range</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  data-testid="filter-min-price"
                  type="number"
                  min="0"
                  value={draft.minPrice}
                  onChange={(e) => setDraft((d) => ({ ...d, minPrice: e.target.value }))}
                  placeholder="Min"
                  className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-price"
                />
                <span className="text-muted-foreground">–</span>
                <input
                  data-testid="filter-max-price"
                  type="number"
                  min="0"
                  value={draft.maxPrice}
                  onChange={(e) => setDraft((d) => ({ ...d, maxPrice: e.target.value }))}
                  placeholder="Max"
                  className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-price"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button data-testid="clear-filters" onClick={clearFilters} className="flex-1 rounded-lg border border-input py-2 text-sm font-medium hover:bg-secondary">Clear</button>
              <button data-testid="apply-filters" onClick={applyFilters} className="flex-1 rounded-lg bg-price py-2 text-sm font-semibold text-white hover:bg-price-light">Apply</button>
            </div>
          </div>
        </>
      )}

      {/* Feed */}
      <div id="listings-feed" className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {isLoading ? (
          <CardSkeletonGrid />
        ) : isError ? (
          <ErrorState onRetry={refetch} />
        ) : listings.length === 0 ? (
          <EmptyState icon={PackageOpen} title="No listings found" message="Try a different category or search term." />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {listings.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} favorited={favoriteIds.includes(l.id)} onToggleFavorite={onFav} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
